import { OperationTypeSchema } from "@repo/shared/rift/operation-types";
import z from "zod";

import type { DateFormat } from "./column-format";

export const DISPLAY_TYPES = ["TABLE", "GRAPH", "STATISTICS"] as const;
export const DisplayTypeSchema = z
  .enum(DISPLAY_TYPES)
  .describe(
    "TABLE for data exploration, GRAPH for trends and charts, STATISTICS for single-value KPIs.",
  );
export type DisplayType = z.infer<typeof DisplayTypeSchema>;

export const GRAPH_TYPES = ["line", "area", "bar", "scatter", "pie"] as const;
export const GraphTypeSchema = z
  .enum(GRAPH_TYPES)
  .describe(
    "line/area for time series, bar for categories, scatter for correlations, pie for proportions.",
  );
export type GraphType = z.infer<typeof GraphTypeSchema>;

// Cartesian chart types (use x/y axes)
export const CARTESIAN_GRAPH_TYPES = [
  "line",
  "area",
  "bar",
  "scatter",
] as const;
export type CartesianGraphType = (typeof CARTESIAN_GRAPH_TYPES)[number];

// Polar chart types (use angle/sector keys)
export const POLAR_GRAPH_TYPES = ["pie"] as const;
export type PolarGraphType = (typeof POLAR_GRAPH_TYPES)[number];

// Stackable chart types
export const STACKABLE_GRAPH_TYPES = ["line", "bar", "area"] as const;
export type StackableGraphType = (typeof STACKABLE_GRAPH_TYPES)[number];

// Chart direction for cartesian charts (bar, line, area)
export const CHART_DIRECTIONS = ["vertical", "horizontal"] as const;
export const ChartDirectionSchema = z
  .enum(CHART_DIRECTIONS)
  .describe("Chart orientation: vertical (default) or horizontal.");
export type ChartDirection = z.infer<typeof ChartDirectionSchema>;

// Legend positions - matches ag-charts
export const LEGEND_POSITIONS = ["top", "right", "bottom", "left"] as const;
export const LegendPositionSchema = z.enum(LEGEND_POSITIONS);
export type LegendPosition = z.infer<typeof LegendPositionSchema>;

// Show values mode for chart series labels
export const ShowValuesModeSchema = z
  .enum(["NONE", "ALWAYS", "HOVER_SERIES", "HOVER_ITEM"])
  .describe(
    "When to show data values on chart points/bars: NONE, ALWAYS, HOVER_SERIES, or HOVER_ITEM.",
  );
export type ShowValuesMode = z.infer<typeof ShowValuesModeSchema>;

// Cartesian series config (LINE, AREA, BAR, SCATTER)
export const CartesianSeriesConfigSchema = z.object({
  xKey: z.string().optional(),
  yKeys: z.array(z.string()).optional(),
});
export type CartesianSeriesConfig = z.infer<typeof CartesianSeriesConfigSchema>;

// Polar series config (PIE, DONUT)
export const PolarSeriesConfigSchema = z.object({
  angleKey: z.string().optional(),
  sectorLabelKey: z.string().optional(),
});
export type PolarSeriesConfig = z.infer<typeof PolarSeriesConfigSchema>;

// Axis label configuration (title text + visibility toggle)
export const AxisLabelConfigSchema = z.object({
  enabled: z.boolean().optional(),
  title: z.string().optional(),
});
export type AxisLabelConfig = z.infer<typeof AxisLabelConfigSchema>;

// Legend configuration
export const LegendConfigSchema = z.object({
  enabled: z.boolean().optional(),
  position: LegendPositionSchema.optional(),
});
export type LegendConfig = z.infer<typeof LegendConfigSchema>;

// Tooltip configuration
export const TooltipConfigSchema = z.object({
  enabled: z.boolean().optional(),
});
export type TooltipConfig = z.infer<typeof TooltipConfigSchema>;

/** Date formats available in the on-chart granularity picker. */
export const DATE_GRANULARITY_PICKER_OPTIONS: DateFormat[] = [
  "DATE",
  "MONTH",
  "YEAR",
];

export const DATE_GRANULARITY_PICKER_LABELS: Partial<
  Record<DateFormat, string>
> = {
  DATE: "Day",
  MONTH: "Month",
  YEAR: "Year",
};

/** Full graph/chart configuration. */
export const GraphConfigSchema = z.object({
  type: GraphTypeSchema,
  xKey: z
    .string()
    .optional()
    .describe(
      "Column for the x-axis (dates for time series, categories for bar charts).",
    ),
  yKeys: z
    .array(z.string())
    .optional()
    .describe("Numeric column(s) to plot on the y-axis."),
  yTypes: z
    .record(z.string(), GraphTypeSchema)
    .optional()
    .describe("Per-series chart type override (yKey -> GraphType)."),
  yColors: z
    .record(z.string(), z.string())
    .optional()
    .describe("Custom hex colors for y-axis series (yKey -> color)."),
  angleKey: z
    .string()
    .optional()
    .describe("Numeric column for pie slice size (polar charts only)."),
  sectorLabelKey: z
    .string()
    .optional()
    .describe("Categorical column for pie slice labels (polar charts only)."),
  stacked: z
    .boolean()
    .optional()
    .describe("Stack series on top of each other (line, bar, area only)."),
  showValues: z
    .record(z.string(), ShowValuesModeSchema)
    .optional()
    .describe("Per-series mode for showing data values on chart elements."),
  direction: ChartDirectionSchema.optional().describe(
    "Chart orientation for cartesian charts (default: vertical).",
  ),
  xAxisLabel: AxisLabelConfigSchema.optional().describe(
    "X-axis label configuration (title text and visibility).",
  ),
  yAxisLabel: AxisLabelConfigSchema.optional().describe(
    "Y-axis label configuration (title text and visibility).",
  ),
  legend: LegendConfigSchema.optional().describe(
    "Legend visibility and position (default: enabled, top).",
  ),
  tooltip: TooltipConfigSchema.optional().describe(
    "Tooltip visibility (default: enabled).",
  ),
  pieSliceLimit: z
    .number()
    .optional()
    .describe(
      'Max pie slices before overflow is bucketed into "Other" (default 10).',
    ),
  xGridLines: z
    .boolean()
    .optional()
    .describe("Show vertical grid lines from the x-axis (default: false)."),
  yGridLines: z
    .boolean()
    .optional()
    .describe("Show horizontal grid lines from the y-axis (default: false)."),
  axisTicks: z
    .boolean()
    .optional()
    .describe("Show tick marks on axes (default: false)."),
  axisLines: z
    .boolean()
    .optional()
    .describe("Show baseline lines along axes (default: false)."),
  dateGranularityPicker: z
    .object({ enabled: z.boolean().optional() })
    .optional()
    .describe("Date granularity picker shown on hover (disabled by default)."),
});
export type GraphConfig = z.infer<typeof GraphConfigSchema>;

/**
 * Partial variant for update paths — every field is optional (including `type`,
 * the lone required field on `GraphConfigSchema`). Clients sync graph edits with
 * partial JSONB patches (e.g. only `xKey`), so the block update route must accept
 * them; the server shallow-merges the patch onto the existing config.
 */
export const GraphConfigUpdateSchema = GraphConfigSchema.partial();
export type GraphConfigUpdate = z.infer<typeof GraphConfigUpdateSchema>;

/**
 * Strict variant of `GraphConfigSchema` that enforces axis fields per chart type.
 *
 * - Cartesian charts (line/area/bar/scatter) require `xKey` and at least one `yKeys` entry.
 * - Polar charts (pie) require `angleKey`.
 *
 * Use this at the agent boundary (structured output + post-validation) where empty
 * axes are an actual error. The base `GraphConfigSchema` stays lenient so the UI's
 * default block state and existing JSONB rows still parse.
 *
 * The inferred TS type is identical to `GraphConfig` — `superRefine` only adds
 * runtime checks, so callers don't need to narrow.
 */
export const GraphConfigStrictSchema = GraphConfigSchema.superRefine(
  (cfg, ctx) => {
    if (isCartesianChart(cfg.type)) {
      if (!cfg.xKey) {
        ctx.addIssue({
          code: "custom",
          path: ["xKey"],
          message:
            "xKey is required for cartesian charts (line/area/bar/scatter).",
        });
      }
      if (!cfg.yKeys?.length) {
        ctx.addIssue({
          code: "custom",
          path: ["yKeys"],
          message:
            "yKeys must contain at least one column for cartesian charts.",
        });
      }
    }
    if (isPolarChart(cfg.type)) {
      if (!cfg.angleKey) {
        ctx.addIssue({
          code: "custom",
          path: ["angleKey"],
          message: "angleKey is required for pie charts.",
        });
      }
    }
  },
);

// Display labels for enum values used in the UI
export const DISPLAY_TYPE_LABELS: Record<DisplayType, string> = {
  TABLE: "Table",
  GRAPH: "Graph",
  STATISTICS: "Stats",
};

export const GRAPH_TYPE_LABELS: Record<GraphType, string> = {
  line: "Line",
  area: "Area",
  bar: "Bar",
  scatter: "Scatter",
  pie: "Pie",
};

export const CHART_DIRECTION_LABELS: Record<ChartDirection, string> = {
  vertical: "Vertical",
  horizontal: "Horizontal",
};

export const LEGEND_POSITION_LABELS: Record<LegendPosition, string> = {
  top: "Top",
  right: "Right",
  bottom: "Bottom",
  left: "Left",
};

export const SHOW_VALUES_MODE_LABELS: Record<ShowValuesMode, string> = {
  NONE: "None",
  ALWAYS: "Always",
  HOVER_SERIES: "Hover series",
  HOVER_ITEM: "Hover item",
};

// ---------------------------------------------------------------------------
// Stats configuration
// ---------------------------------------------------------------------------

export const STAT_SIZES = ["SMALL", "MEDIUM", "LARGE"] as const;
export const StatSizeSchema = z
  .enum(STAT_SIZES)
  .describe("Display size for statistics blocks.");
export type StatSize = z.infer<typeof StatSizeSchema>;

export const STAT_SIZE_LABELS: Record<StatSize, string> = {
  SMALL: "Small",
  MEDIUM: "Medium",
  LARGE: "Large",
};

export const STAT_FORMATS = ["long", "short"] as const;
export const StatFormatSchema = z
  .enum(STAT_FORMATS)
  .describe(
    "Number format: long for full numbers, short for abbreviated (e.g. 1.2K).",
  );
export type StatFormat = z.infer<typeof StatFormatSchema>;

export const STAT_FORMAT_LABELS: Record<StatFormat, string> = {
  long: "Long",
  short: "Short",
};

export const DeltaModeSchema = z
  .enum(["ABSOLUTE", "PERCENTAGE", "ABS_PERCENT", "PREV_VALUE"])
  .describe(
    "Delta display mode: ABSOLUTE for difference, PERCENTAGE for %, ABS_PERCENT for both, PREV_VALUE for raw previous value.",
  );
export type DeltaMode = z.infer<typeof DeltaModeSchema>;

export const DELTA_MODE_LABELS: Record<DeltaMode, string> = {
  ABSOLUTE: "Difference",
  PERCENTAGE: "Percentage",
  ABS_PERCENT: "Both",
  PREV_VALUE: "Value",
};

/** Per-stat configuration for showing period-over-period deltas. */
export const StatDeltaConfigSchema = z.object({
  enabled: z.boolean().describe("Whether to show the delta comparison."),
  mode: DeltaModeSchema.default("PERCENTAGE"),
  invertColors: z
    .boolean()
    .optional()
    .describe("Swap green/red colors (e.g. when lower is better)."),
});
export type StatDeltaConfig = z.infer<typeof StatDeltaConfigSchema>;

/** A single statistic entry displayed in a statistics block. */
export const StatItemSchema = z.object({
  columnId: z.string().describe("The column name to compute the statistic on."),
  operation: OperationTypeSchema,
  label: z
    .string()
    .optional()
    .describe("Custom display label (defaults to column name)."),
  format: StatFormatSchema.optional(),
  delta: StatDeltaConfigSchema.optional().describe(
    "Period-over-period delta comparison config.",
  ),
});
export type StatItem = z.infer<typeof StatItemSchema>;

/** Configuration for a statistics display block. */
export const StatConfigSchema = z.object({
  items: z
    .array(StatItemSchema)
    .default([])
    .describe(
      "Statistic entries to display, each with a column and operation.",
    ),
  size: StatSizeSchema.default("SMALL"),
  format: StatFormatSchema.default("long"),
});
export type StatConfig = z.infer<typeof StatConfigSchema>;

/**
 * Partial variant for update paths — all fields are optional with no defaults,
 * so Zod won't inflate missing keys and the merge helper can distinguish
 * "not provided" from "explicitly set".
 */
export const StatConfigUpdateSchema = z.object({
  items: z
    .array(StatItemSchema)
    .optional()
    .describe(
      "Statistic entries to display, each with a column and operation.",
    ),
  size: StatSizeSchema.optional(),
  format: StatFormatSchema.optional(),
});
export type StatConfigUpdate = z.infer<typeof StatConfigUpdateSchema>;

// ---------------------------------------------------------------------------
// Table configuration
// ---------------------------------------------------------------------------

/** Configuration for a table display block. */
export const TableConfigSchema = z.object({
  quickSearch: z
    .boolean()
    .optional()
    .describe("Enable inline quick-search filtering in the table header."),
});
export type TableConfig = z.infer<typeof TableConfigSchema>;

// Helper functions to check chart type categories
export const isCartesianChart = (type: GraphType): type is CartesianGraphType =>
  (CARTESIAN_GRAPH_TYPES as readonly string[]).includes(type);

export const isPolarChart = (type: GraphType): type is PolarGraphType =>
  (POLAR_GRAPH_TYPES as readonly string[]).includes(type);

export const isStackableChart = (type: GraphType): type is StackableGraphType =>
  (STACKABLE_GRAPH_TYPES as readonly string[]).includes(type);

// ---------------------------------------------------------------------------
// Agent-facing slim variants
//
// Anthropic's strict structured-output / tool grammar caps the number of
// omittable properties at 24 per request, and Zod `.optional()` /
// `.default(...)` both produce omittable properties. These slim variants
// keep only the analytically meaningful fields and make every property
// required (with `.nullable()` for the genuinely optional ones), so the
// total optional count stays at zero. Chrome/styling fields (grid lines,
// axis ticks, tooltip toggles, axis labels, legend position, etc.) are
// user-controlled tweaks made through the inspector UI after the block
// lands; the renderer already has sensible defaults for them.
// ---------------------------------------------------------------------------

/**
 * Agent-facing variants of the nested chrome sub-objects. Inner properties are
 * REQUIRED (not nullable): the parent field is `.optional()` on
 * `GraphConfigAgentSchema`, so the agent omits the whole object to leave it
 * unchanged and emits a complete object to set it. Keeping the inner props
 * non-nullable adds zero union-typed parameters (Anthropic caps these at 16
 * per request).
 */
const LegendConfigAgentSchema = z.object({
  enabled: z.boolean(),
  position: LegendPositionSchema,
});

const AxisLabelConfigAgentSchema = z.object({
  enabled: z.boolean(),
  title: z.string(),
});

const TooltipConfigAgentSchema = z.object({ enabled: z.boolean() });

const DateGranularityPickerAgentSchema = z.object({
  enabled: z.boolean(),
});

/** Slim graph configuration the AI agent emits at block creation/update. */
export const GraphConfigAgentSchema = z.object({
  type: GraphTypeSchema,
  xKey: z
    .string()
    .nullable()
    .describe(
      "Column for the x-axis (dates for time series, categories for bar charts). Required for cartesian charts; emit null for pie charts.",
    ),
  yKeys: z
    .array(z.string())
    .nullable()
    .describe(
      "Numeric column(s) to plot on the y-axis. Required for cartesian charts; emit null for pie charts.",
    ),
  yTypes: z
    .record(z.string(), GraphTypeSchema)
    .optional()
    .describe(
      "Per-series chart-type override for combo charts (yKey -> line/area/bar/scatter). Omit to leave unchanged.",
    ),
  yColors: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      'Per-series hex colors, keyed by the exact yKey/projected column name (e.g. { "revenue::SUM": "#2563eb" }). Cartesian charts only — has no effect on pie charts. Omit to leave colors unchanged.',
    ),
  angleKey: z
    .string()
    .nullable()
    .describe(
      "Numeric column for pie slice size (polar charts only). Emit null for cartesian charts.",
    ),
  sectorLabelKey: z
    .string()
    .nullable()
    .describe(
      "Categorical column for pie slice labels (polar charts only). Emit null for cartesian charts.",
    ),
  stacked: z
    .boolean()
    .nullable()
    .describe(
      "Stack series on top of each other (line/bar/area). Emit null to use the default (unstacked).",
    ),
  showValues: z
    .record(z.string(), ShowValuesModeSchema)
    .optional()
    .describe(
      "Per-series data-label mode (yKey -> NONE/ALWAYS/HOVER_SERIES/HOVER_ITEM). Omit to leave unchanged.",
    ),
  direction: ChartDirectionSchema.optional().describe(
    "Cartesian chart orientation: vertical (default) or horizontal. Omit to leave unchanged.",
  ),
  xAxisLabel: AxisLabelConfigAgentSchema.optional().describe(
    "X-axis title text + visibility (emit the complete object). Omit to leave unchanged.",
  ),
  yAxisLabel: AxisLabelConfigAgentSchema.optional().describe(
    "Y-axis title text + visibility (emit the complete object). Omit to leave unchanged.",
  ),
  legend: LegendConfigAgentSchema.optional().describe(
    "Legend visibility + position (top/right/bottom/left); emit the complete object. Omit to leave unchanged.",
  ),
  tooltip: TooltipConfigAgentSchema.optional().describe(
    "Tooltip visibility (emit the complete object). Omit to leave unchanged.",
  ),
  pieSliceLimit: z
    .number()
    .optional()
    .describe(
      'Max pie slices before the rest is bucketed into "Other". Omit to leave unchanged.',
    ),
  xGridLines: z
    .boolean()
    .optional()
    .describe(
      "Show vertical grid lines from the x-axis. Omit to leave unchanged.",
    ),
  yGridLines: z
    .boolean()
    .optional()
    .describe(
      "Show horizontal grid lines from the y-axis. Omit to leave unchanged.",
    ),
  axisTicks: z
    .boolean()
    .optional()
    .describe("Show tick marks on axes. Omit to leave unchanged."),
  axisLines: z
    .boolean()
    .optional()
    .describe("Show baseline lines along axes. Omit to leave unchanged."),
  dateGranularityPicker: DateGranularityPickerAgentSchema.optional().describe(
    "On-hover date granularity picker visibility (emit the complete object). Omit to leave unchanged.",
  ),
});
export type GraphConfigAgent = z.infer<typeof GraphConfigAgentSchema>;

/**
 * Strict variant of `GraphConfigAgentSchema` that enforces axis fields per
 * chart type. Cartesian charts require `xKey` + at least one `yKeys` entry;
 * polar charts require `angleKey`.
 */
export const GraphConfigAgentStrictSchema = GraphConfigAgentSchema.superRefine(
  (cfg, ctx) => {
    if (isCartesianChart(cfg.type)) {
      if (!cfg.xKey) {
        ctx.addIssue({
          code: "custom",
          path: ["xKey"],
          message:
            "xKey is required for cartesian charts (line/area/bar/scatter).",
        });
      }
      if (!cfg.yKeys?.length) {
        ctx.addIssue({
          code: "custom",
          path: ["yKeys"],
          message:
            "yKeys must contain at least one column for cartesian charts.",
        });
      }
    }
    if (isPolarChart(cfg.type)) {
      if (!cfg.angleKey) {
        ctx.addIssue({
          code: "custom",
          path: ["angleKey"],
          message: "angleKey is required for pie charts.",
        });
      }
    }
  },
);

/**
 * Agent-facing variant of `StatDeltaConfigSchema`. Lives inside the
 * `statConfig.items` array (array-nested, so it doesn't count toward the
 * request-level union/omittable caps); emitted as a complete object when set.
 */
const StatDeltaConfigAgentSchema = z.object({
  enabled: z.boolean().describe("Whether to show the delta comparison."),
  mode: DeltaModeSchema,
  invertColors: z
    .boolean()
    .optional()
    .describe("Swap green/red colors (e.g. when lower is better)."),
});

/** Slim per-stat entry for AI agent emission. */
export const StatItemAgentSchema = z.object({
  columnId: z.string().describe("The projected column name to compute on."),
  operation: OperationTypeSchema,
  label: z
    .string()
    .nullable()
    .describe(
      "Custom display label. Emit null to fall back to the column name.",
    ),
  format: StatFormatSchema.nullable().describe(
    "Per-item number format override. Emit null to inherit the block-level format.",
  ),
  delta: StatDeltaConfigAgentSchema.optional().describe(
    "Period-over-period delta comparison (emit the complete object). Omit for no delta.",
  ),
});
export type StatItemAgent = z.infer<typeof StatItemAgentSchema>;

/** Slim statistics-block configuration for AI agent emission. */
export const StatConfigAgentSchema = z.object({
  items: z
    .array(StatItemAgentSchema)
    .describe("Statistic entries to display. Emit `[]` for none."),
  size: StatSizeSchema.describe(
    "Block display size. Default to SMALL for almost all stats blocks — SMALL keeps numbers readable and lets multiple stats fit and wrap without being cut off. Reserve MEDIUM/LARGE for a single hero metric the user explicitly wants emphasized (one item only).",
  ),
  format: StatFormatSchema,
});
export type StatConfigAgent = z.infer<typeof StatConfigAgentSchema>;

/** Slim table-block configuration for AI agent emission. */
export const TableConfigAgentSchema = z.object({
  quickSearch: z
    .boolean()
    .nullable()
    .describe(
      "Enable inline quick-search filtering in the table header. Emit null to keep the default.",
    ),
});
export type TableConfigAgent = z.infer<typeof TableConfigAgentSchema>;
